import { retrieveFactoryDetail, retrieveFactoryInfo } from '@/api/factory'
import { addFavorite, removeFavorite } from '@/api/favorite'
import { usePlugin } from '@/hooks/usePlugin'
import { useUserStore } from '@/pinia/user'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { useLoginModal } from '../Login'
import { buriedPointUtil, isMobile } from '@/utils/utils'

export const useFactory = (factoryId) => {
  const { $toast } = usePlugin()
  const queryClient = useQueryClient()
  const user = useUserStore()
  const { open: openLoginModal } = useLoginModal()
  const location = ref('Top') // 用于埋点

  const { isPending, data: factory, isError } = useQuery({
    queryKey: ['/appb/b2b/supplier/get', factoryId],
    queryFn: async () => {
      const data = await retrieveFactoryInfo(factoryId.value)
      if (data == null) throw new Error('Factory not found.')
      return data
    },
    enabled: computed(() => factoryId.value != null),
    retry: 1
  })
  useQuery({
    queryKey: ['/appb/b2b/supplier/detail', factoryId],
    queryFn: async () => {
      const data = await retrieveFactoryDetail(factoryId.value)
      if (data == null) throw new Error('Factory not found.')
      queryClient.setQueryData(['/appb/b2b/supplier/get', factoryId], data)
      return data
    },
    enabled: computed(() => factory.value != null && factory.value.imperfect),
    refetchInterval: (query) => {
      return query.state.data?.imperfect
    },
    retry: 1
  })
  const loading = computed(() => isPending.value || isError.value)

  const isFollowed = computed(() => factory.value ? factory.value.currentUserCollect : false)

  const follow = async () => {
    try {
      const { code } = await addFavorite({
        type: 'SUPPLIER',
        userCollectSaveSupplierVo: {
          sellerOpenId: factoryId.value
        }
      })
      if (code === 200) {
        queryClient.setQueryData(['/appb/b2b/supplier/get', factoryId], (oldData) => ({
          ...oldData,
          currentUserCollect: true
        }))
        $toast('Followed successfully.')

        buriedPointUtil({
          eventId: isMobile() ? 'HPD211' : 'PPD211',
          others: {
            type: 'Follow',
            location: location.value,
            followKey: factoryId.value
          }
        })
      }
    } catch (e) {
      $toast(e.message, { type: 'error' })
      throw e
    }
  }

  const unfollow = async () => {
    try {
      const { code } = await removeFavorite({
        type: 'SUPPLIER',
        targetId: factoryId.value
      })
      if (code === 200) {
        queryClient.setQueryData(['/appb/b2b/supplier/get', factoryId], (oldData) => ({
          ...oldData,
          currentUserCollect: false
        }))
        buriedPointUtil({
          eventId: isMobile() ? 'HPD211' : 'PPD211',
          others: {
            type: 'Followed',
            location: location.value,
            followKey: factoryId.value
          }
        })
      }
    } catch (e) {
      $toast(e.message, { type: 'error' })
      throw e
    }
  }

  const toggleFollow = async (local) => {
    if (!user.isLogged) {
      openLoginModal()
      return
    }
    location.value = local
    if (isFollowed.value) {
      await unfollow()
    } else {
      await follow()
    }
  }

  return { loading, factory, isFollowed, toggleFollow }
}
